import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NotificationBell } from "@/components/NotificationBell";
import { LayoutDashboard, Users, Calculator, CheckSquare, FileSignature, ClipboardCheck, FolderOpen, Building2, Settings as SettingsIcon, LogOut, Menu, Clock, ArrowRight, Radar, Lock, CalendarClock, Inbox } from "lucide-react";
import logo from "../assets/galahadji-logo.png";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/prospecting", label: "Prospecting", icon: Radar },
  { to: "/deal-analyzer", label: "Deal Analyzer", icon: Calculator },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/schedule", label: "Schedule", icon: CalendarClock },
  { to: "/offers", label: "Offers", icon: FileSignature },
  { to: "/due-diligence", label: "Due Diligence", icon: ClipboardCheck },
  { to: "/documents", label: "Documents", icon: FolderOpen },
  { to: "/forms", label: "Forms", icon: ClipboardCheck },
  { to: "/portfolio", label: "Portfolio", icon: Building2 },
  { to: "/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

function SidebarBody({ user, onNavigate, onLogout, badges = {} }) {
  const loc = useLocation();
  return (
    <div className="h-full flex flex-col bg-primary text-primary-foreground">
      <div className="px-6 py-6 border-b border-white/10">
        <Link to="/dashboard" onClick={onNavigate} className="flex items-center" data-testid="sidebar-logo">
          <img src={logo} alt="Galahadji Acquisitions" className="h-36 w-auto object-contain" />
        </Link>
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon }) => {
          const active = loc.pathname === to || (to !== "/dashboard" && loc.pathname.startsWith(to));
          const badge = badges[to];
          return (
            <Link key={to} to={to} onClick={onNavigate} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 px-6 min-h-[44px] py-2.5 text-sm transition-colors duration-150 border-l-2 ${active ? "border-accent bg-white/10 text-white font-medium" : "border-transparent text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Icon className="w-4 h-4 shrink-0" /> <span className="flex-1">{label}</span>
              {badge > 0 && (
                <span data-testid={`nav-badge-${label.toLowerCase().replace(/ /g, "-")}`}
                  className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-accent-foreground text-[11px] font-bold leading-none">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="px-6 py-4 border-t border-white/10">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.first_name} {user?.last_name}</div>
            <div className="text-[11px] uppercase tracking-wider text-accent">{user?.role}</div>
          </div>
          {!onNavigate && <NotificationBell variant="light" />}
        </div>
        <button onClick={onLogout} data-testid="logout-btn" className="flex items-center gap-2 min-h-[44px] mt-1 text-sm text-white/60 hover:text-white transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

function TrialBanner() {
  const [plan, setPlan] = useState(null);
  useEffect(() => { api.get("/plan").then((r) => setPlan(r.data)).catch(() => {}); }, []);
  if (!plan || plan.plan_status !== "trialing" || plan.trial_days_left == null) return null;
  if (plan.trial_expired) {
    return (
      <div data-testid="trial-expired-banner" className="flex flex-wrap items-center justify-between gap-2 bg-destructive/10 border-b border-destructive/30 px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <Lock className="w-4 h-4 shrink-0" />
          <span className="font-medium">Your trial has ended — the workspace is read-only until you upgrade.</span>
        </div>
        <Link to="/pricing" data-testid="trial-upgrade-btn" className="inline-flex items-center gap-1 min-h-[36px] px-3 rounded-md bg-destructive text-destructive-foreground text-xs font-semibold hover:bg-destructive/90 transition-colors">
          Upgrade to unlock <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }
  const days = plan.trial_days_left;
  return (
    <div data-testid="trial-banner" className="flex flex-wrap items-center justify-between gap-2 bg-accent/10 border-b border-accent/30 px-4 sm:px-6 lg:px-8 py-2.5">
      <div className="flex items-center gap-2 text-sm text-foreground">
        <Clock className="w-4 h-4 text-accent shrink-0" />
        <span className="font-medium">{days === 0 ? "Your trial ends today" : `${days} day${days === 1 ? "" : "s"} left in your trial`}</span>
      </div>
      <Link to="/pricing" data-testid="trial-upgrade-btn" className="inline-flex items-center gap-1 min-h-[36px] px-3 rounded-md bg-accent text-accent-foreground text-xs font-semibold hover:bg-accent/90 transition-colors">
        Upgrade now <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [enquiryCount, setEnquiryCount] = useState(0);

  const handleLogout = async () => { await logout(); nav("/login"); };

  useEffect(() => {
    let stop = false;
    const fetchCount = () => api.get("/contact-submissions/count")
      .then((r) => { if (!stop) setEnquiryCount(r.data?.new || 0); })
      .catch(() => { if (!stop) setEnquiryCount(0); });
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [loc.pathname]);

  const badges = { "/enquiries": enquiryCount };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col fixed inset-y-0 z-30" data-testid="sidebar">
        <SidebarBody user={user} onLogout={handleLogout} badges={badges} />
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-primary text-primary-foreground px-4 h-16 pt-[env(safe-area-inset-top)]" data-testid="mobile-header">
        <Link to="/dashboard" className="flex items-center">
          <img src={logo} alt="Galahadji Acquisitions" className="h-14 w-auto object-contain" />
        </Link>
        <div className="flex items-center gap-1">
          <NotificationBell variant="light" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" aria-label="Open menu" className="inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-md hover:bg-white/10 transition-colors">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 border-0" data-testid="mobile-drawer">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">Main navigation links for the Galahadji Acquisition OS</SheetDescription>
              <SidebarBody user={user} onLogout={handleLogout} badges={badges} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="lg:ml-64 min-h-screen pb-[env(safe-area-inset-bottom)]">
        <TrialBanner />
        {children}
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="sticky top-16 lg:top-0 z-20 backdrop-blur-xl bg-background/80 border-b border-border px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground truncate" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
